'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import { TagCategory } from '@/types/searchSuggestions';
import { HighlightedSuggestion } from '@/components/Header/HighlightedSuggestion';
import { SuggestionLinkItem } from '@/components/Header/SuggestionLinkItem';
import { getMPListPageHrefArgs, getShowroomHrefArgs } from '@/utility/urls';
type TagSuggestionItemProps = {
  /** The raw tag or completed phrase, used for tracking. */
  expression: string;
  /** `{…}`-delimited highlight template of the phrase. */
  highlighted: string;
  category: TagCategory;
  /** Navigates to the category-filtered list page — also resets the search field. */
  onNavigate: () => void;
  /** Renders the group heading above the first row of a group. */
  groupLabel?: string;
  /** Separates the last row from the group below. */
  showDivider?: boolean;
  /** When set, the row links to this license showroom rather than the category-filtered PLP. */
  licenseShowroomSlug?: string;
};

/**
 * A phrase that named a product kind, so it links straight to that category's list page. Shared by
 * the enriched tag rows and the half-typed "dog hoo" → "dog hoodie" completions — same shape, and
 * the completions are the group that carries a heading.
 */
export const TagSuggestionItem = t0 => {
  const $ = _c(24);
  const {
    expression,
    highlighted,
    category,
    onNavigate,
    groupLabel,
    showDivider,
    licenseShowroomSlug
  } = t0;
  const t = useTranslations("header");
  let t1;
  if ($[0] !== category.searchTerm || $[1] !== category.slugs || $[2] !== licenseShowroomSlug) {
    t1 = licenseShowroomSlug ? getShowroomHrefArgs({
      userSlug: licenseShowroomSlug
    }) : getMPListPageHrefArgs({
      categorySlugs: category.slugs,
      searchTerm: category.searchTerm
    });
    $[0] = category.searchTerm;
    $[1] = category.slugs;
    $[2] = licenseShowroomSlug;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = `Searchbar - Tag - ${expression} - ${category.name}`;
  let t3;
  if ($[4] !== groupLabel) {
    t3 = groupLabel && <span className="sr-only">{groupLabel}: </span>;
    $[4] = groupLabel;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== highlighted) {
    t4 = <HighlightedSuggestion>{highlighted}</HighlightedSuggestion>;
    $[6] = highlighted;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t3 || $[9] !== t4) {
    t5 = <span className="min-w-24 truncate">{t3}{t4}</span>;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== category.name || $[12] !== t) {
    t6 = t("suggestionInCategory", {
      category: category.name
    });
    $[11] = category.name;
    $[12] = t;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t6) {
    t7 = <span className="ml-auto min-w-0 truncate pl-2 text-sm text-neutral-700">{t6}</span>;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== groupLabel || $[17] !== onNavigate || $[18] !== showDivider || $[19] !== t1 || $[20] !== t2 || $[21] !== t5 || $[22] !== t7) {
    t8 = <SuggestionLinkItem href={t1} groupLabel={groupLabel} showDivider={showDivider} onNavigate={onNavigate} dataTracking={t2}>{t5}{t7}</SuggestionLinkItem>;
    $[16] = groupLabel;
    $[17] = onNavigate;
    $[18] = showDivider;
    $[19] = t1;
    $[20] = t2;
    $[21] = t5;
    $[22] = t7;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  return t8;
};